import { useEffect, useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import ResultsPanel from '../ResultsPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ConditionTree from '../ConditionTree.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import useUserDraft from '../../../utils/useUserDraft.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

const PRICE = ['close', 'open', 'high', 'low', 'volume'];
const INITIAL = { indicators: [], entry: { all: [{ left: 'close', op: '>', right: 'open' }] }, exit: { all: [{ left: 'close', op: '<', right: 'open' }] } };

export default function RulesBuilderMode({ config, onConfigChange, onRan, lastRun, onSaveRun, onRulesStrategyChange }) {
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');
  const [params, setParams] = useState({});
  const [lag, setLag] = useState(0);
  const [draft, setDraft] = useUserDraft('rules', INITIAL);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    backtestApi.indicators().then((data) => {
      if (!alive) return;
      const fixed = (data.indicators || []).filter((item) => item.kind === 'technical' && item.group !== 'relative')
        .map((item) => ({ ...item, id: `feature:${item.key}`, ref: { feature: item.key } }));
      const dynamic = (data.parameterized || []).map((item) => ({ ...item, id: `indicator:${item.key}`, ref: { indicator: item.key } }));
      setCatalog([...dynamic, ...fixed]);
    }).catch(() => alive && setCatalogError('Indicator catalog unavailable. No fallback indicators have been substituted.'));
    return () => { alive = false; };
  }, []);
  const item = catalog.find((c) => c.id === selected);
  const strategy = { side: 'long', entry: draft.entry, exit: draft.exit,
    sl_pct: config.slPct ? config.slPct / 100 : null, pt_pct: config.ptPct ? config.ptPct / 100 : null,
    max_hold_bars: config.maxHoldBars || null, one_per_day: true };
  useEffect(() => { onRulesStrategyChange?.(strategy); }, [JSON.stringify(strategy), onRulesStrategyChange]);
  const operands = [...PRICE.map((field) => ({ label: field, ref: field })), ...draft.indicators];
  function choose(id) {
    setSelected(id);
    const next = catalog.find((c) => c.id === id);
    setParams(Object.fromEntries(Object.entries(next?.parameters || {}).map(([key, spec]) => [key, spec.default])));
  }
  function add() {
    if (!item) return;
    const ref = { ...item.ref, ...params, ...(lag ? { lag } : {}) };
    if (draft.indicators.some((i) => JSON.stringify(i.ref) === JSON.stringify(ref))) return;
    setDraft({ ...draft, indicators: [...draft.indicators, { ref, label: `${item.key} ${Object.entries(params).map(([k, v]) => `${k}=${v}`).join(', ')}${lag ? ` lag=${lag}` : ''}` }] });
  }
  async function run() {
    setRunning(true); setError(null);
    try {
      const data = await backtestApi.run({ mode: 'rules', strategy, symbols: config.symbols, period: config.period, config: engineConfig(config) });
      setResult(data); onSaveRun?.(data); onRan?.();
    } catch (err) { setError(err); } finally { setRunning(false); }
  }
  return <div className="aq-grid aq-grid--2">
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ConfigPanel config={config} onChange={onConfigChange} />
      <Card>
        <h3>Technical indicators</h3>
        <p className="aq-module-desc">Parameters and lag are in bars. Fundamental and benchmark-dependent rules require historical data and are not enabled here.</p>
        {catalogError && <p role="alert">{catalogError}</p>}
        <input aria-label="Search indicators" placeholder="Search indicator or category" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Indicator" value={selected} onChange={(e) => choose(e.target.value)}>
          <option value="">Choose an indicator</option>
          {catalog.filter((c) => `${c.key} ${c.description} ${c.group}`.toLowerCase().includes(search.toLowerCase())).map((c) =>
            <option key={c.id} value={c.id}>{c.group} · {c.description}{c.parameters ? ' (custom)' : ' (preset)'}</option>)}
        </select>
        {Object.entries(item?.parameters || {}).map(([key, spec]) => <label key={key}>{key}
          <input type="number" min={spec.min} max={spec.max} step={spec.type === 'integer' ? 1 : 'any'} value={params[key] ?? spec.default} onChange={(e) => setParams({ ...params, [key]: Number(e.target.value) })} />
        </label>)}
        <label>Lag (0 = current completed bar)<input type="number" min="0" max="2000" step="1" value={lag} onChange={(e) => setLag(Number(e.target.value))} /></label>
        <Button size="sm" disabled={!item} onClick={add}>Add operand</Button>
        <div className="aq-indicator-chip-row">{draft.indicators.map((i, index) => <span className="aq-symbol-chip" key={JSON.stringify(i.ref)}>{i.label}
          <button aria-label={`Remove ${i.label}`} onClick={() => setDraft({ ...draft, indicators: draft.indicators.filter((_, n) => n !== index) })}>×</button>
        </span>)}</div>
        <h4>Entry conditions</h4>
        <ConditionTree node={draft.entry} operands={operands} onChange={(entry) => setDraft({ ...draft, entry })} />
        <h4>Exit conditions</h4>
        <ConditionTree node={draft.exit} operands={operands} onChange={(exit) => setDraft({ ...draft, exit })} />
        <StrategyPreview title="Engine strategy" value={strategy} />
        <Button disabled={running || !config.symbols.length || !(config.capital > 0) || !isBacktestPeriodValid(config.period)} onClick={run}>{running ? 'Submitting…' : 'Run rules backtest'}</Button>
      </Card>
    </div>
    <ResultsPanel result={result} error={error} running={running} />
  </div>;
}
